import { ExpediaHotelOrigin, ThirdPartyAncillariesProvider } from '@fcom/common';

import { CountryConfigMap, ExpediaLocaleData } from './interfaces';
import { ExpediaSupportedCountry } from './enums';

export const MANAGE_BOOKING_FEATURE_KEY = 'manage-booking';

export const STATIC_OLD_MMB_POST_PARAMS = {
  SITE: 'FINRFINR',
  PAGE: 'BKGD',
};

const prefix = 'apis-update';
export const OPEN_TRAVEL_PREPARATIONS_NOTIFICATION = `${prefix}_open-travel-preparations-notification`;
export const CANCEL_APIS_UPDATE = `${prefix}-details_apis-cancel`;
export const FULL_APIS_UPDATE = `${prefix}-details-full_apis-success`;
export const PARTIAL_APIS_UPDATE = `${prefix}-details-partial_apis-success`;
export const ERROR_APIS_UPDATE = `${prefix}-details_apis-error`;
export const APIS_REQUIRED = `${prefix}_required`;
export const USER_OTHER_ADDRESS_APIS = `${prefix}-use-other-address_apis`;

export const GTM_BOOKING_WIDGET_ELEMENT_IDS = {
  [ThirdPartyAncillariesProvider.EXPEDIA_HOTEL]: 'booking-widget-tab-button_hotels',
  [ThirdPartyAncillariesProvider.CAR_TRAWLER_RENTAL]: 'booking-widget-tab-button_cartrawler-rental',
};

export const mapToGaString = {
  [ThirdPartyAncillariesProvider.CAR_TRAWLER_RENTAL]: 'third-party-box_cartrawler-rental',
  [ThirdPartyAncillariesProvider.CAR_TRAWLER_TRANSFER]: 'third-party-box_cartrawler-transfer',
  [ThirdPartyAncillariesProvider.EXPEDIA_HOTEL]: 'third-party-box_expedia-hotel',
};

export const mapToGaState = {
  [ThirdPartyAncillariesProvider.CAR_TRAWLER_RENTAL]: 'company: cartrawler',
  [ThirdPartyAncillariesProvider.CAR_TRAWLER_TRANSFER]: 'company: cartrawler',
  [ThirdPartyAncillariesProvider.EXPEDIA_HOTEL]: 'company: expedia',
};

export const carTrawlerSmartBlockConfig = {
  partnerName: 'finnair',
  CTProduct: 'widget',
};

export const localeToLanguageAndCurrency: Record<string, ExpediaLocaleData> = {
  fi_FI: { languageCode: 'fi_FI', currencyCode: 'EUR' },
  en_FI: { languageCode: 'en_GB', currencyCode: 'EUR' },
  de_DE: { languageCode: 'de_DE', currencyCode: 'EUR' },
  en_DE: { languageCode: 'en_GB', currencyCode: 'EUR' },
  de_AT: { languageCode: 'de_DE', currencyCode: 'EUR' },
  de_CH: { languageCode: 'de_DE', currencyCode: 'EUR' },
  en_GB: { languageCode: 'en_GB', currencyCode: 'GBP' },
  en_IE: { languageCode: 'en_IE', currencyCode: 'EUR' },
  sv_SE: { languageCode: 'sv_SE', currencyCode: 'SEK' },
  en_SE: { languageCode: 'en_GB', currencyCode: 'SEK' },
  en_AU: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_AT: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_BE: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_CA: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_CN: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_CZ: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_DK: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_EE: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_FR: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_HK: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_HU: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_IS: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_IN: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_IL: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_IT: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_JP: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_KR: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_LV: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_LT: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_NL: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_NO: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_PL: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_SG: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_ES: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_CH: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_TH: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en_US: { languageCode: 'en_IE', currencyCode: 'EUR' },
  en: { languageCode: 'en_IE', currencyCode: 'EUR' },
};

export const expediaPartnersConfig: Partial<Record<ExpediaHotelOrigin, CountryConfigMap>> = {
  [ExpediaHotelOrigin.TRIP_OVERVIEW]: {
    [ExpediaSupportedCountry.Finland]: {
      signatureId: 'MMB_TRIP_OVERVIEW_FI',
      partnerId: 'finnair-finland-external-mmb-trips',
    },
    [ExpediaSupportedCountry.Germany]: {
      signatureId: 'MMB_TRIP_OVERVIEW_DE',
      partnerId: 'finnair-germany-external-mmb-trips',
    },
    [ExpediaSupportedCountry.Ireland]: {
      signatureId: 'MMB_TRIP_OVERVIEW_IE',
      partnerId: 'finnair-ireland-external-mmb-trips',
    },
    [ExpediaSupportedCountry.UnitedKingdom]: {
      signatureId: 'MMB_TRIP_OVERVIEW_UK',
      partnerId: 'finnair-uk-external-mmb-trips',
    },
    [ExpediaSupportedCountry.Sweden]: {
      signatureId: 'MMB_TRIP_OVERVIEW_SE',
      partnerId: 'finnair-sweden-external-mmb-trips',
    },
  },
  [ExpediaHotelOrigin.THIRD_PARTY_ANCILLARIES]: {
    [ExpediaSupportedCountry.Finland]: {
      signatureId: 'MMB_THIRD_PARTY_ANCILLARIES_PAGE_FI',
      partnerId: 'finnair-finland-external-mmb-destserv',
    },
    [ExpediaSupportedCountry.Germany]: {
      signatureId: 'MMB_THIRD_PARTY_ANCILLARIES_PAGE_DE',
      partnerId: 'finnair-germany-external-mmb-destserv',
    },
    [ExpediaSupportedCountry.Ireland]: {
      signatureId: 'MMB_THIRD_PARTY_ANCILLARIES_PAGE_IE',
      partnerId: 'finnair-ireland-external-mmb-destserv',
    },
    [ExpediaSupportedCountry.UnitedKingdom]: {
      signatureId: 'MMB_THIRD_PARTY_ANCILLARIES_PAGE_UK',
      partnerId: 'finnair-uk-external-mmb-destserv',
    },
    [ExpediaSupportedCountry.Sweden]: {
      signatureId: 'MMB_THIRD_PARTY_ANCILLARIES_PAGE_SE',
      partnerId: 'finnair-sweden-external-mmb-destserv',
    },
  },
  [ExpediaHotelOrigin.ANCILLARY_CONFIRMATION]: {
    [ExpediaSupportedCountry.Finland]: {
      signatureId: 'MMB_ANCILLARY_CONFIRMATION_FI',
      partnerId: 'finnair-finland-external-conf',
    },
    [ExpediaSupportedCountry.Germany]: {
      signatureId: 'MMB_ANCILLARY_CONFIRMATION_DE',
      partnerId: 'finnair-germany-external-conf',
    },
    [ExpediaSupportedCountry.Ireland]: {
      signatureId: 'MMB_ANCILLARY_CONFIRMATION_IE',
      partnerId: 'finnair-ireland-external-conf',
    },
    [ExpediaSupportedCountry.UnitedKingdom]: {
      signatureId: 'MMB_ANCILLARY_CONFIRMATION_UK',
      partnerId: 'finnair-uk-external-conf',
    },
    [ExpediaSupportedCountry.Sweden]: {
      signatureId: 'MMB_ANCILLARY_CONFIRMATION_SE',
      partnerId: 'finnair-sweden-external-conf',
    },
  },
};
